import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Text } from '@cencosud-cencommerce/paris-uikit';

import { useAppSelector } from '@/Application/Hooks/Redux';
import { CardInput, CardSelect } from '@/modules/perseo-billing/components';
import type { TInvoiceValues } from '@/modules/perseo-billing/interfaces';
import type { Region } from '@/modules/region/domain/Region';
import useRegions from '@/sections/region/hooks/useRegions';

import { formatRut } from './format-rut';
import { useGetCommunesFromRegion } from './get-available-communes-from-region';
import useIsFieldValid from './use-is-field-valid';

interface ISelect {
  value: string;
  name: string;
}

const VALUE_DEFAULT_OPTIONS = {
  shouldDirty: true,
  shouldValidate: true,
  shouldTouch: true
};

const getRegionsOptions = (regions: Region[]) => {
  return regions.map((option) => ({
    value: option.id.toString(),
    name: option.name
  }));
};

export const FormFields = () => {
  const { data: cart } = useAppSelector((state) => state.cart);
  const { data: regions } = useRegions();

  const [regionsOptions, setRegionsOptions] = useState<ISelect[]>([]);
  const [communesOptions, setCommunesOptions] = useState<ISelect[]>([]);

  const form = useFormContext<TInvoiceValues>();
  const { setValue, watch, register } = form;
  const { errors } = form.formState;
  const billingAddress = cart?.billingAddress;

  const idField = useIsFieldValid('id');
  const officialNameField = useIsFieldValid('officialName');
  const areaOfWorkField = useIsFieldValid('areaOfWork');
  const addressField = useIsFieldValid('address');
  const addressNumberField = useIsFieldValid('addressNumber');
  const numberOfHouseApartmentOrOfficeField = useIsFieldValid(
    'numberOfHouseApartmentOrOffice'
  );
  const regionId = watch('regionId');
  const communeNameField = watch('communeId');

  const availableCommunesByRegion = useGetCommunesFromRegion({
    regionId
  });

  const handleRutChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedRut = formatRut(e.target.value || '');
    setValue('id', formattedRut, VALUE_DEFAULT_OPTIONS);
  };

  useEffect(() => {
    const REGIONS_TO_SELECT = getRegionsOptions(regions);
    setRegionsOptions(REGIONS_TO_SELECT);
  }, [regions]);

  useEffect(() => {
    setCommunesOptions(availableCommunesByRegion);
  }, [availableCommunesByRegion, regionId]);

  useEffect(() => {
    if (!billingAddress?.isInvoice) return;
    const id = billingAddress?.companyIdentityDocumentNumber ?? '';
    const streetFullName = `${billingAddress?.streetName} ${billingAddress?.streetNumber}`;
    const officialName = billingAddress?.companyBusinessName ?? '';
    const areaOfWork = billingAddress?.companyLineOfBusiness ?? '';
    const numberOfHouseApartmentOrOffice =
      billingAddress?.additionalAddressInfo ?? '';
    const regionId = String(billingAddress?.region) ?? '';
    const communeId = billingAddress?.commune ?? '';

    setValue('id', id, VALUE_DEFAULT_OPTIONS);
    setValue('address', streetFullName, VALUE_DEFAULT_OPTIONS);
    setValue('officialName', officialName, VALUE_DEFAULT_OPTIONS);
    setValue('areaOfWork', areaOfWork, VALUE_DEFAULT_OPTIONS);
    setValue(
      'numberOfHouseApartmentOrOffice',
      numberOfHouseApartmentOrOffice,
      VALUE_DEFAULT_OPTIONS
    );
    setValue('regionId', regionId, VALUE_DEFAULT_OPTIONS);
    setValue('communeId', communeId, VALUE_DEFAULT_OPTIONS);
  }, [billingAddress, setValue]);

  return (
    <>
      <CardInput
        label={'RUT empresa'}
        labelSize="body1"
        maxLength={12}
        {...idField}
        {...register('id', {
          onChange: (e) => handleRutChange(e)
        })}
      />

      <CardInput
        label={'Razón social'}
        labelSize="body1"
        {...officialNameField}
        {...register('officialName')}
      />

      <CardInput
        label={'Giro'}
        labelSize="body1"
        {...areaOfWorkField}
        {...register('areaOfWork')}
      />
      <fieldset className="co-flex co-flex-col co-w-full co-gap-2">
        <legend className="co-pb-2">
          <Text variant="subtitle2" weight={500}>
            Dirección tributaria
          </Text>
        </legend>
        <CardInput
          label={'Nombre dirección tributaria'}
          example='Ejemplo: "Av. Providencia'
          labelSize="body1"
          {...addressField}
          {...register('address')}
          maxLength={65}
        />

        <CardInput
          label={'Número dirección tributaria'}
          example='Ejemplo: "3823'
          labelSize="body1"
          {...addressNumberField}
          {...register('addressNumber')}
          maxLength={8}
        />
      </fieldset>

      <CardInput
        inputMode="numeric"
        type="text"
        label={'Número de departamento, casa u oficina'}
        labelSize="body1"
        {...numberOfHouseApartmentOrOfficeField}
        {...register('numberOfHouseApartmentOrOffice')}
        maxLength={75}
      />

      <CardSelect
        color={regionId ? 'success' : 'default'}
        error={errors?.regionId?.message?.toString() ?? ''}
        options={regionsOptions}
        label={'Región'}
        labelSize="body2"
        {...register('regionId')}
      />

      <CardSelect
        color={communeNameField ? 'success' : 'default'}
        error={errors?.communeId?.message?.toString()}
        options={communesOptions}
        label={'Comuna'}
        labelSize="body2"
        {...register('communeId')}
      />
    </>
  );
};
